import moment from 'moment'

import { SubscriptionStatus, type BillingSummary } from '@/model/Billing'

export const CHECKOUT_TRIAL_WARNING_DAYS = 15
export const DEFAULT_CHECKOUT_TRIAL_DAYS = 30

export function daysUntilCheckoutTrialEndUtc(checkoutTrialEndsAt: string): number {
  const end = moment.utc(checkoutTrialEndsAt).startOf('day')
  const now = moment.utc().startOf('day')
  return end.diff(now, 'days')
}

export function daysSinceCreationUtc(creationDate: string): number {
  const created = moment.utc(creationDate).startOf('day')
  const now = moment.utc().startOf('day')
  return now.diff(created, 'days')
}

export function isPastDefaultTrialWithoutManualEnd(params: {
  billingCompanyCreationDate?: string
  manualTrialSubscriptionEnd?: string | null
  trialDays?: number
}): boolean {
  if (params.manualTrialSubscriptionEnd) {
    return false
  }
  if (!params.billingCompanyCreationDate) {
    return false
  }
  const trialDays = params.trialDays ?? DEFAULT_CHECKOUT_TRIAL_DAYS
  return daysSinceCreationUtc(params.billingCompanyCreationDate) >= trialDays
}

export type CheckoutTrialWarningInput = {
  stripeBillingEnabled: boolean
  hasActiveSubscription: boolean
  checkoutTrialEndsAt?: string
  billingCompanyCreationDate?: string
  manualTrialSubscriptionEnd?: string | null
  trialDays?: number
  warningDays?: number
}

export function shouldShowCheckoutTrialWarning(params: CheckoutTrialWarningInput): boolean {
  const warningDays = params.warningDays ?? CHECKOUT_TRIAL_WARNING_DAYS
  if (!params.stripeBillingEnabled || params.hasActiveSubscription) {
    return false
  }
  if (
    isPastDefaultTrialWithoutManualEnd({
      billingCompanyCreationDate: params.billingCompanyCreationDate,
      manualTrialSubscriptionEnd: params.manualTrialSubscriptionEnd,
      trialDays: params.trialDays
    })
  ) {
    return true
  }
  if (!params.checkoutTrialEndsAt) {
    return false
  }
  const days = daysUntilCheckoutTrialEndUtc(params.checkoutTrialEndsAt)
  return days >= 0 && days <= warningDays
}

export function resolveCheckoutTrialWarningDaysRemaining(
  params: CheckoutTrialWarningInput
): number | null {
  if (!shouldShowCheckoutTrialWarning(params)) {
    return null
  }
  if (params.checkoutTrialEndsAt) {
    return daysUntilCheckoutTrialEndUtc(params.checkoutTrialEndsAt)
  }
  if (
    isPastDefaultTrialWithoutManualEnd({
      billingCompanyCreationDate: params.billingCompanyCreationDate,
      manualTrialSubscriptionEnd: params.manualTrialSubscriptionEnd,
      trialDays: params.trialDays
    })
  ) {
    return 0
  }
  return null
}

export type SubscriptionHeroDateKind = 'nextPayment' | 'trialEnd'

export type SubscriptionHeroDate = {
  kind: SubscriptionHeroDateKind
  isoDate: string
}

export function resolveSubscriptionHeroDate(summary: BillingSummary): SubscriptionHeroDate | null {
  if (summary.hasActiveSubscription) {
    if (summary.status === SubscriptionStatus.TRIALING) {
      const isoDate = summary.trialEndsAt ?? summary.currentPeriodEnd
      return isoDate ? { kind: 'trialEnd', isoDate } : null
    }
    if (summary.currentPeriodEnd) {
      return { kind: 'nextPayment', isoDate: summary.currentPeriodEnd }
    }
    return null
  }
  if (summary.checkoutTrialEndsAt) {
    return { kind: 'trialEnd', isoDate: summary.checkoutTrialEndsAt }
  }
  return null
}

export function checkoutTrialWarningParamsFromSummary(
  summary: {
    hasActiveSubscription: boolean
    checkoutTrialEndsAt?: string
    billingCompanyCreationDate?: string
    manualTrialSubscriptionEnd?: string | null
    trialDays?: number
  },
  stripeBillingEnabled: boolean
): CheckoutTrialWarningInput {
  return {
    stripeBillingEnabled,
    hasActiveSubscription: summary.hasActiveSubscription === true,
    checkoutTrialEndsAt: summary.checkoutTrialEndsAt,
    billingCompanyCreationDate: summary.billingCompanyCreationDate,
    manualTrialSubscriptionEnd: summary.manualTrialSubscriptionEnd,
    trialDays: summary.trialDays
  }
}
